import { buildExpressFundingPayload, type ExpressFundingItem } from './express-funding-payload'
import type { ExpressJointApplicantBasicInfo } from './express-joint-applicant-draft'
import { formatPhoneForBasicDisplay } from './phone-format'
import { debitCardDesignIdOrDefault } from '@/components/apply/debit-card-design'
import type { SharedAccountType } from './account-types'
import type { ImpactDonationChoice } from './application-types'
import {
  hasApplicationDonationSelection,
  inferRequiresDonationFromResumeSummary,
  readResumeImpactDonation,
} from './impact-donation'
import { marketingForExpressSnapshot } from './express-marketing-query.ts'
import type { MarketingInfo } from './application-types.ts'
import type { BeneficiariesFormValues } from './beneficiary-form-schema'
import { parseFundingAmount } from './plaid-funding'

export const EXPRESS_JOINT_NEW_FLOW = 'express_joint_new'
export const EXPRESS_APPLICATION_ID_STORAGE_KEY = 'express_application_id'

export type ExpressBeneficiaryPayload = {
  id: string
  firstName: string
  lastName: string
  relationship: string
  percentage: string
  address: BeneficiariesFormValues['beneficiaries'][number]['address']
}

export function mapBeneficiariesForExpressPayload(
  beneficiaries: BeneficiariesFormValues['beneficiaries'],
): ExpressBeneficiaryPayload[] {
  return beneficiaries.map((beneficiary) => ({
    id: beneficiary.id,
    firstName: beneficiary.firstName,
    lastName: beneficiary.lastName,
    relationship: beneficiary.relationship,
    percentage: beneficiary.percentage,
    address: beneficiary.address,
  }))
}

export type ExpressJointProgressStep =
  | 'intro'
  | 'joint-basic'
  | 'joint-id'
  | 'joint-employment'
  | 'donation-selection'
  | 'beneficiaries'
  | 'review'

type GenericMap = Record<string, unknown>

export type ExpressJointProgressSnapshotInput = {
  applicationId: string
  email: string
  phone: string
  primaryExistingCustomer: boolean
  name: string
  dob: string
  ssn: string
  wantsJointApplicant: boolean
  step: ExpressJointProgressStep
  wizardSteps: ExpressJointProgressStep[]
  jointFirstName: string
  jointLastName: string
  jointEmail: string
  jointPhone: string
  jointApplicantBasic: ExpressJointApplicantBasicInfo | null
  jointAgreeToTerms: boolean
  jointDob: string
  jointSsn: string
  jointStreet: string
  jointApartment: string
  jointCity: string
  jointState: string
  jointZip: string
  jointGender: string
  jointPersonaInquiryId: string | null
  jointPersonaVerified: boolean
  jointIdNumber: string
  jointIdState: string
  jointIdIssueDate: string
  jointIdExpirationDate: string
  jointIdAddressMatches: string
  jointEmployment: {
    employmentStatus: string
    employerName: string
    jobTitle: string
    annualIncome: string
  }
  beneficiaries: BeneficiariesFormValues['beneficiaries']
  funding: ExpressFundingItem[]
  npsScore?: number | null
  referralSource?: string
  referralSpecify?: string
  impactDonation?: ImpactDonationChoice | null
  marketing?: MarketingInfo
}

export type ExpressJointHydrationPatch = {
  applicationId: string
  step: ExpressJointProgressStep
  email: string
  phone: string
  name: string
  dob: string
  ssn: string
  wantsJointApplicant: boolean
  jointChoiceMade: boolean
  jointExistingLookupComplete: boolean
  jointExistingCustomer: boolean
  jointFirstName: string
  jointLastName: string
  jointEmail: string
  jointPhone: string
  jointApplicantBasic: ExpressJointApplicantBasicInfo | null
  jointAgreeToTerms: boolean
  jointDob: string
  jointSsn: string
  jointStreet: string
  jointApartment: string
  jointCity: string
  jointState: string
  jointZip: string
  jointGender: string
  jointPersonaInquiryId: string | null
  jointPersonaVerified: boolean
  jointPersonaSmsSent: boolean
  jointIdNumber: string
  jointIdState: string
  jointIdIssueDate: string
  jointIdExpirationDate: string
  jointIdAddressMatches: string
  jointEmployment: ExpressJointProgressSnapshotInput['jointEmployment']
  beneficiaries: ExpressJointProgressSnapshotInput['beneficiaries']
  funding: ExpressFundingItem[]
  npsScore: number | null
  referralSource: string
  referralSpecify: string
}

function toRecord(value: unknown): GenericMap | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as GenericMap)
    : null
}

function coerceString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

export function createExpressDraftApplicationId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  return `express_${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`
}

export function readStoredExpressApplicationId(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const stored = window.sessionStorage.getItem(EXPRESS_APPLICATION_ID_STORAGE_KEY)
    return stored?.trim() || null
  } catch {
    return null
  }
}

export function writeStoredExpressApplicationId(applicationId: string | null): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    if (applicationId) {
      window.sessionStorage.setItem(EXPRESS_APPLICATION_ID_STORAGE_KEY, applicationId)
    } else {
      window.sessionStorage.removeItem(EXPRESS_APPLICATION_ID_STORAGE_KEY)
    }
  } catch {
    // Non-fatal.
  }
}

export function isExpressJointNewSnapshot(snapshot: GenericMap | null | undefined): boolean {
  const meta = toRecord(snapshot?.meta)
  return meta?.express_flow === EXPRESS_JOINT_NEW_FLOW
}

export function resolveExpressStepFromSnapshot(
  snapshot: GenericMap,
  wizardSteps: ExpressJointProgressStep[]
): ExpressJointProgressStep {
  const meta = toRecord(snapshot.meta)
  const expressStep = coerceString(meta?.express_step)
  if (wizardSteps.includes(expressStep as ExpressJointProgressStep)) {
    return expressStep as ExpressJointProgressStep
  }
  const currentStep = Number(meta?.current_step)
  if (Number.isFinite(currentStep) && currentStep > 0) {
    return wizardSteps[Math.min(currentStep - 1, wizardSteps.length - 1)] ?? 'intro'
  }
  return 'intro'
}

export function buildExpressJointProgressSnapshot(
  input: ExpressJointProgressSnapshotInput,
  status: 'draft' | 'in_progress' = 'in_progress'
): GenericMap {
  const nameParts = input.name.trim().split(/\s+/)
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''
  const jointBasic = input.jointApplicantBasic ?? {
    firstName: input.jointFirstName.trim(),
    lastName: input.jointLastName.trim(),
    email: input.jointEmail.trim(),
    phoneNumber: formatPhoneForBasicDisplay(input.jointPhone),
    agreeToTerms: input.jointAgreeToTerms,
  }
  const currentIndex = Math.max(0, input.wizardSteps.indexOf(input.step))
  const reachedSteps = input.wizardSteps
    .slice(0, currentIndex + 1)
    .map((_, index) => index + 1)

  return {
    applicationId: input.applicationId,
    status,
    has_joint_applicant: input.wantsJointApplicant,
    primary_applicant: input.primaryExistingCustomer
      ? {
          existingCustomer: true,
          basic_info: {
            agreeToTerms: true,
            email: input.email,
            phoneNumber: formatPhoneForBasicDisplay(input.phone),
          },
          personal_info: null,
          identity_info: null,
          employment_info: null,
        }
      : {
          existingCustomer: false,
          basic_info: {
            firstName,
            lastName,
            email: input.email,
            phoneNumber: formatPhoneForBasicDisplay(input.phone),
            agreeToTerms: true,
          },
          personal_info: {
            streetAddress: '',
            city: '',
            state: '',
            zipCode: '',
            dateOfBirth: input.dob,
            socialSecurityNumber: input.ssn,
          },
          identity_info: null,
          employment_info: null,
        },
    joint_applicant: {
      existingCustomer: false,
      basic_info: {
        firstName: jointBasic.firstName,
        lastName: jointBasic.lastName,
        email: jointBasic.email,
        phoneNumber: jointBasic.phoneNumber,
        agreeToTerms: jointBasic.agreeToTerms,
      },
      personal_info: {
        streetAddress: input.jointStreet,
        apartmentUnit: input.jointApartment || undefined,
        city: input.jointCity,
        state: input.jointState,
        zipCode: input.jointZip,
        dateOfBirth: input.jointDob,
        gender: input.jointGender,
        socialSecurityNumber: input.jointSsn,
      },
      identity_info: input.jointPersonaInquiryId || input.jointPersonaVerified || input.jointIdNumber
        ? {
            personaConsent: true,
            personaInquiryId: input.jointPersonaInquiryId || undefined,
            personaVerified: input.jointPersonaVerified,
            idNumber: input.jointIdNumber,
            idState: input.jointIdState,
            issueDate: input.jointIdIssueDate,
            expirationDate: input.jointIdExpirationDate,
            idAddressMatches: input.jointIdAddressMatches,
          }
        : null,
      employment_info: input.jointEmployment.employmentStatus
        ? {
            employmentStatus: input.jointEmployment.employmentStatus,
            employerName: input.jointEmployment.employerName || undefined,
            jobTitle: input.jointEmployment.jobTitle || undefined,
            annualIncome: input.jointEmployment.annualIncome || undefined,
          }
        : null,
    },
    beneficiaries: mapBeneficiariesForExpressPayload(input.beneficiaries),
    funding: {
      products: input.funding.map((entry) => ({
        accountId: entry.accountId,
        title: entry.title,
        category: entry.type,
        amount: entry.amount ?? '',
        method: entry.method ?? '',
        otherMethod: entry.otherMethod,
        primaryDebitCard: entry.primaryDebitCard,
        primaryDebitCardDesignId: entry.primaryDebitCardDesignId,
        jointDebitCard: entry.jointDebitCard,
        jointDebitCardDesignId: entry.jointDebitCardDesignId,
        minimumDeposit: entry.minimumDeposit,
        fundingMethodsAllowed: entry.fundingMethodsAllowed,
        accountTermsUrl: entry.accountTermsUrl,
      })),
      total_initial_deposit: input.funding
        .reduce((sum, entry) => sum + Number(String(entry.amount || '0').replace(/[^0-9.-]/g, '') || 0), 0)
        .toFixed(2),
    },
    meta: {
      express_flow: EXPRESS_JOINT_NEW_FLOW,
      express_step: input.step,
      completed_steps: reachedSteps,
      reached_steps: reachedSteps,
      current_step: currentIndex + 1,
      last_saved_at: new Date().toISOString(),
    },
    nps_score: input.npsScore ?? null,
    referral_source: input.referralSource || null,
    referral_specify: input.referralSpecify?.trim() || null,
    ...(input.impactDonation ? { impact_donation: input.impactDonation } : {}),
    marketing: marketingForExpressSnapshot(input.marketing),
    email: input.email,
    product: 'impact-checking',
  }
}

/** Minimal express one-pager save before Plaid; server enriches BaaS applicants. */
export function buildExpressSoloFundingProgressSnapshot(input: {
  applicationId: string
  email: string
  phone?: string
  primaryExistingCustomer: boolean
  hasJointApplicant: boolean
  jointExistingCustomer?: boolean
  jointAgreeToTerms?: boolean
  funding: ExpressFundingItem[]
  meta: GenericMap
  marketing?: MarketingInfo
}): GenericMap {
  const phoneDisplay = input.phone ? formatPhoneForBasicDisplay(input.phone) : undefined
  return {
    applicationId: input.applicationId,
    has_joint_applicant: input.hasJointApplicant,
    primary_applicant: {
      existingCustomer: input.primaryExistingCustomer,
      basic_info: {
        agreeToTerms: true,
        email: input.email,
        ...(phoneDisplay ? { phoneNumber: phoneDisplay } : {}),
      },
    },
    ...(input.hasJointApplicant
      ? {
          joint_applicant: {
            existingCustomer: input.jointExistingCustomer === true,
            basic_info: {
              agreeToTerms: input.jointAgreeToTerms === true,
            },
          },
        }
      : {}),
    funding: {
      products: input.funding.map((entry) => ({
        accountId: entry.accountId,
        title: entry.title,
        category: entry.type,
        amount: entry.amount ?? '',
        method: entry.method ?? '',
        otherMethod: entry.otherMethod,
        primaryDebitCard: entry.primaryDebitCard,
        primaryDebitCardDesignId: entry.primaryDebitCardDesignId,
        jointDebitCard: entry.jointDebitCard,
        jointDebitCardDesignId: entry.jointDebitCardDesignId,
        minimumDeposit: entry.minimumDeposit,
        fundingMethodsAllowed: entry.fundingMethodsAllowed,
        accountTermsUrl: entry.accountTermsUrl,
      })),
    },
    meta: input.meta,
    marketing: marketingForExpressSnapshot(input.marketing),
  }
}

export function buildExpressJointHydrationPatch(
  snapshot: GenericMap,
  wizardSteps: ExpressJointProgressStep[]
): ExpressJointHydrationPatch | null {
  if (!isExpressJointNewSnapshot(snapshot)) {
    return null
  }

  const primary = toRecord(snapshot.primary_applicant)
  const primaryBasic = toRecord(primary?.basic_info)
  const primaryPersonal = toRecord(primary?.personal_info)
  const joint = toRecord(snapshot.joint_applicant)
  const jointBasic = toRecord(joint?.basic_info)
  const jointPersonal = toRecord(joint?.personal_info)
  const jointIdentity = toRecord(joint?.identity_info)
  const jointEmployment = toRecord(joint?.employment_info)
  const fundingObj = toRecord(snapshot.funding)
  const fundingProducts = Array.isArray(fundingObj?.products) ? fundingObj.products : []
  const beneficiaries = Array.isArray(snapshot.beneficiaries) ? snapshot.beneficiaries : []

  const jointFirstName = coerceString(jointBasic?.firstName)
  const jointLastName = coerceString(jointBasic?.lastName)
  const jointEmail = coerceString(jointBasic?.email)
  const jointPhone = formatPhoneForBasicDisplay(coerceString(jointBasic?.phoneNumber))
  const jointApplicantBasic =
    jointFirstName && jointLastName && jointEmail && jointPhone
      ? {
          firstName: jointFirstName,
          lastName: jointLastName,
          email: jointEmail,
          phoneNumber: jointPhone,
          agreeToTerms: jointBasic?.agreeToTerms === true,
        }
      : null

  return {
    applicationId: coerceString(snapshot.applicationId),
    step: resolveExpressStepFromSnapshot(snapshot, wizardSteps),
    email: coerceString(primaryBasic?.email) || coerceString(snapshot.email),
    phone: formatPhoneForBasicDisplay(coerceString(primaryBasic?.phoneNumber)),
    name: [coerceString(primaryBasic?.firstName), coerceString(primaryBasic?.lastName)].filter(Boolean).join(' '),
    dob: coerceString(primaryPersonal?.dateOfBirth),
    ssn: coerceString(primaryPersonal?.socialSecurityNumber),
    wantsJointApplicant: snapshot.has_joint_applicant === true,
    jointChoiceMade: true,
    jointExistingLookupComplete: true,
    jointExistingCustomer: joint?.existingCustomer === true,
    jointFirstName,
    jointLastName,
    jointEmail,
    jointPhone,
    jointApplicantBasic,
    jointAgreeToTerms: jointBasic?.agreeToTerms === true,
    jointDob: coerceString(jointPersonal?.dateOfBirth),
    jointSsn: coerceString(jointPersonal?.socialSecurityNumber),
    jointStreet: coerceString(jointPersonal?.streetAddress),
    jointApartment: coerceString(jointPersonal?.apartmentUnit),
    jointCity: coerceString(jointPersonal?.city),
    jointState: coerceString(jointPersonal?.state),
    jointZip: coerceString(jointPersonal?.zipCode),
    jointGender: coerceString(jointPersonal?.gender),
    jointPersonaInquiryId: null,
    jointPersonaVerified: jointIdentity?.personaVerified === true,
    jointPersonaSmsSent: jointIdentity?.personaVerified === true,
    jointIdNumber: coerceString(jointIdentity?.idNumber),
    jointIdState: coerceString(jointIdentity?.idState),
    jointIdIssueDate: coerceString(jointIdentity?.issueDate),
    jointIdExpirationDate: coerceString(jointIdentity?.expirationDate),
    jointIdAddressMatches:
      jointIdentity?.idAddressMatches === true
        ? 'yes'
        : jointIdentity?.idAddressMatches === false
          ? 'no'
          : coerceString(jointIdentity?.idAddressMatches),
    jointEmployment: {
      employmentStatus: coerceString(jointEmployment?.employmentStatus),
      employerName: coerceString(jointEmployment?.employerName),
      jobTitle: coerceString(jointEmployment?.jobTitle),
      annualIncome: coerceString(jointEmployment?.annualIncome),
    },
    beneficiaries: beneficiaries
      .map((entry, index) => {
        const row = toRecord(entry)
        const address = toRecord(row?.address)
        if (!row) {
          return null
        }
        return {
          id: coerceString(row.id) || `beneficiary-${index}`,
          firstName: coerceString(row.firstName),
          lastName: coerceString(row.lastName),
          relationship: coerceString(row.relationship),
          address: {
            street: coerceString(address?.street ?? address?.streetAddress),
            city: coerceString(address?.city),
            state: coerceString(address?.state),
            zip: coerceString(address?.zip ?? address?.zipCode),
            country: coerceString(address?.country) || 'US',
          },
        }
      })
      .filter((entry): entry is ExpressJointHydrationPatch['beneficiaries'][number] => Boolean(entry)),
    funding: fundingProducts
      .map((entry): ExpressFundingItem | null => {
        const row = toRecord(entry)
        if (!row) {
          return null
        }
        const accountId = coerceString(row.accountId)
        const title = coerceString(row.title)
        const type = coerceString(row.category ?? row.type)
        if (!accountId || !title || !type) {
          return null
        }
        return {
          accountId,
          title,
          type,
          amount: coerceString(row.amount),
          method: coerceString(row.method),
          otherMethod: coerceString(row.otherMethod) || undefined,
          primaryDebitCard: row.primaryDebitCard === true,
          primaryDebitCardDesignId: coerceString(row.primaryDebitCardDesignId) || undefined,
          jointDebitCard: row.jointDebitCard === true,
          jointDebitCardDesignId: coerceString(row.jointDebitCardDesignId) || undefined,
          minimumDeposit: typeof row.minimumDeposit === 'number' ? row.minimumDeposit : undefined,
          fundingMethodsAllowed: coerceString(row.fundingMethodsAllowed) || undefined,
          accountTermsUrl: coerceString(row.accountTermsUrl) || undefined,
        }
      })
      .filter((entry): entry is ExpressFundingItem => entry !== null),
    npsScore: typeof snapshot.nps_score === 'number' ? snapshot.nps_score : null,
    referralSource: coerceString(snapshot.referral_source),
    referralSpecify: coerceString(snapshot.referral_specify),
  }
}

export async function saveExpressApplicationProgress(input: {
  applicationId: string
  email: string
  currentStep: number
  status?: 'draft' | 'in_progress'
  application: GenericMap
  jointLookupToken?: string
}): Promise<{ applicationId: string; recovered?: boolean }> {
  const response = await fetch('/api/applications/progress', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({
      email: input.email,
      applicationId: input.applicationId,
      currentStep: input.currentStep,
      status: input.status ?? 'in_progress',
      applicationType: 'express',
      application: input.application,
      ...(input.jointLookupToken ? { jointLookupToken: input.jointLookupToken } : {}),
    }),
  })
  const json = (await response.json().catch(() => null)) as {
    ok?: boolean
    error?: string
    data?: { applicationId?: string; recovered?: boolean }
  } | null
  if (!response.ok || !json?.ok || !json.data?.applicationId) {
    throw new Error(json?.error || 'Unable to save express application progress.')
  }
  return {
    applicationId: json.data.applicationId,
    recovered: json.data.recovered === true,
  }
}

export function buildExpressWizardSteps(options: {
  requiresDonation: boolean
  jointNeedsDetails: boolean
  isOnePagerFlow: boolean
}): ExpressJointProgressStep[] {
  if (options.isOnePagerFlow || !options.jointNeedsDetails) {
    return options.requiresDonation
      ? ['intro', 'donation-selection', 'review']
      : ['intro', 'review']
  }
  const base: ExpressJointProgressStep[] = [
    'intro',
    'joint-basic',
    'joint-id',
    'joint-employment',
    'beneficiaries',
    'review',
  ]
  if (!options.requiresDonation) {
    return base
  }
  const reviewIndex = base.lastIndexOf('review')
  return [
    ...base.slice(0, reviewIndex),
    'donation-selection',
    ...base.slice(reviewIndex),
  ]
}

export const EXPRESS_JOINT_WIZARD_STEPS: ExpressJointProgressStep[] = [
  'intro',
  'joint-basic',
  'joint-id',
  'joint-employment',
  'beneficiaries',
  'review',
]

export const EXPRESS_JOINT_STEP_LABELS: Record<ExpressJointProgressStep, string> = {
  intro: 'Joint Applicant',
  'joint-basic': 'Joint Basic Info',
  'joint-id': 'Joint Identity',
  'joint-employment': 'Joint Employment',
  'donation-selection': 'Donation Selection',
  beneficiaries: 'Beneficiaries & Funding',
  review: 'Review & Submit',
}

/** Non-PII structural summary returned by the server for in-progress express apps. */
export type ExpressResumeSummary = {
  completedSteps: number[]
  reachedSteps: number[]
  currentStep: number
  hasJointApplicant: boolean
  primary: { basic: boolean; personal: boolean; identity: boolean; employment: boolean }
  joint?: { basic: boolean; personal: boolean; identity: boolean; employment: boolean }
  beneficiaryCount: number
  funding: {
    productCodes: string[]
    productTitles: string[]
    accountCount: number
    hasLinkedBank: boolean
    plaid_link?: {
      institution_name: string
      account_name: string
      account_mask: string
      account_type: 'CHK' | 'SAV'
      linked_at?: string
    }
    products: Array<{
      accountId: string
      title: string
      category: string
      amount: string
      method: string
      otherMethod?: string
      primaryDebitCard?: boolean
      primaryDebitCardDesignId?: string
      jointDebitCard?: boolean
      jointDebitCardDesignId?: string
      minimumDeposit?: number
      fundingMethodsAllowed?: string
      accountTermsUrl?: string
    }>
  }
  identityVerified: { primary: boolean; joint?: boolean }
  personaVerificationStarted?: { primary: boolean; joint?: boolean }
  expressFlow?: string | null
  expressStep?: string | null
  requiresDonationSelection?: boolean
  donationSelectionAccounts?: Array<{
    accountId: string
    description: string
    instanceNumber: number
  }>
  impactDonation?: ImpactDonationChoice | null
}

export function expressStepToNumber(
  step: ExpressJointProgressStep,
  steps: ExpressJointProgressStep[] = EXPRESS_JOINT_WIZARD_STEPS,
): number {
  const index = steps.indexOf(step)
  return index >= 0 ? index + 1 : 1
}

export function expressStepFromNumber(
  stepNumber: number,
  steps: ExpressJointProgressStep[] = EXPRESS_JOINT_WIZARD_STEPS,
): ExpressJointProgressStep {
  return steps[Math.max(0, Math.min(stepNumber - 1, steps.length - 1))] ?? 'intro'
}

export function normalizeExpressJointResumeSteps(
  summary: ExpressResumeSummary,
  options?: {
    requiresDonation?: boolean
    jointNeedsDetails?: boolean
    isOnePagerFlow?: boolean
    wizardSteps?: ExpressJointProgressStep[]
  },
): {
  completedSteps: number[]
  reachedSteps: number[]
  currentStep: number
} {
  const requiresDonation = options?.requiresDonation
    ?? inferRequiresDonationFromResumeSummary(summary)
  const jointNeedsDetails = options?.jointNeedsDetails
    ?? (summary.expressFlow === EXPRESS_JOINT_NEW_FLOW && summary.hasJointApplicant)
  const isOnePagerFlow = options?.isOnePagerFlow ?? false
  const wizardSteps = options?.wizardSteps ?? buildExpressWizardSteps({
    requiresDonation,
    jointNeedsDetails,
    isOnePagerFlow,
  })

  const completedSteps = new Set<number>()
  const beneficiariesStep = expressStepToNumber('beneficiaries', wizardSteps)
  const reviewStep = expressStepToNumber('review', wizardSteps)
  const donationStep = expressStepToNumber('donation-selection', wizardSteps)

  if (summary.expressFlow === EXPRESS_JOINT_NEW_FLOW && summary.hasJointApplicant) {
    completedSteps.add(expressStepToNumber('intro', wizardSteps))
  }
  if (summary.joint?.basic) {
    completedSteps.add(expressStepToNumber('joint-basic', wizardSteps))
  }
  if (summary.joint?.personal && summary.identityVerified.joint) {
    completedSteps.add(expressStepToNumber('joint-id', wizardSteps))
  }
  if (summary.joint?.employment) {
    completedSteps.add(expressStepToNumber('joint-employment', wizardSteps))
  }
  if (
    requiresDonation && (
      summary.completedSteps.includes(donationStep) ||
      hasApplicationDonationSelection(readResumeImpactDonation(summary.impactDonation))
    )
  ) {
    completedSteps.add(donationStep)
  }
  if (
    summary.completedSteps.includes(beneficiariesStep) ||
    (summary.funding.products.length > 0 && summary.completedSteps.some((step) => step >= beneficiariesStep))
  ) {
    completedSteps.add(beneficiariesStep)
  }

  const sortedCompletedSteps = [...completedSteps].sort((a, b) => a - b)
  const firstIncomplete = wizardSteps.find((stepName) => {
    const stepNumber = expressStepToNumber(stepName, wizardSteps)
    return stepName !== 'review' && !completedSteps.has(stepNumber)
  })
  const firstIncompleteStep = firstIncomplete
    ? expressStepToNumber(firstIncomplete, wizardSteps)
    : reviewStep
  const savedCurrent = summary.currentStep
  const currentStep = wizardSteps.some((stepName) => expressStepToNumber(stepName, wizardSteps) === savedCurrent)
    ? savedCurrent
    : firstIncompleteStep
  const maxReachedStep = Math.min(reviewStep, Math.max(currentStep, ...sortedCompletedSteps, 1))
  const reachedSteps = Array.from({ length: maxReachedStep }, (_, index) => index + 1)

  return {
    completedSteps: sortedCompletedSteps,
    reachedSteps,
    currentStep,
  }
}

export function buildExpressResumeFundingRows(
  summary: ExpressResumeSummary,
  accountTypes: SharedAccountType[],
): ExpressFundingItem[] {
  const accountTypeById = new Map(accountTypes.map((accountType) => [accountType.id, accountType]))
  const findCatalogAccount = (accountId: string) => {
    const direct = accountTypeById.get(accountId)
    if (direct) { return direct }
    const baseAccountId = accountId.replace(/-\d+$/, '')
    return accountTypeById.get(baseAccountId)
  }

  const products = summary.funding.products.length > 0
    ? summary.funding.products
    : summary.funding.productCodes.map((productCode, index) => {
        const catalogAccount = findCatalogAccount(productCode)
        return {
          accountId: productCode,
          title: summary.funding.productTitles[index] || catalogAccount?.title || productCode,
          category: catalogAccount?.category || 'checking',
          amount: '0',
          method: '',
          otherMethod: undefined,
          primaryDebitCard: false,
          primaryDebitCardDesignId: undefined,
          jointDebitCard: false,
          jointDebitCardDesignId: undefined,
          minimumDeposit: catalogAccount?.minimumDeposit,
          fundingMethodsAllowed: catalogAccount?.fundingMethodsAllowed,
          accountTermsUrl: catalogAccount?.accountTermsUrl,
        }
      })

  return products.map((product, index) => {
    const catalogAccount = findCatalogAccount(product.accountId)
    const title = product.title ||
      catalogAccount?.title ||
      summary.funding.productTitles[index] ||
      product.accountId

    return {
      accountId: product.accountId,
      title,
      type: product.category || catalogAccount?.category || 'checking',
      amount: product.amount,
      method: product.method,
      otherMethod: product.otherMethod ?? undefined,
      primaryDebitCard: product.primaryDebitCard ?? false,
      primaryDebitCardDesignId: product.primaryDebitCard
        ? debitCardDesignIdOrDefault(product.primaryDebitCardDesignId)
        : undefined,
      jointDebitCard: product.jointDebitCard ?? false,
      jointDebitCardDesignId: product.jointDebitCard
        ? debitCardDesignIdOrDefault(product.jointDebitCardDesignId)
        : undefined,
      minimumDeposit: product.minimumDeposit ?? catalogAccount?.minimumDeposit,
      fundingMethodsAllowed: product.fundingMethodsAllowed ?? catalogAccount?.fundingMethodsAllowed,
      accountTermsUrl: product.accountTermsUrl ?? catalogAccount?.accountTermsUrl,
    }
  })
}

export type ExpressJointSectionSaveItem = {
  kind: 'basic' | 'personal' | 'identity' | 'employment' | 'beneficiaries' | 'funding' | 'impact_donation'
  applicant: 'primary' | 'joint'
  data: unknown
  has_joint_applicant?: boolean
}

export async function saveExpressJointSectionProgress(input: {
  applicationId: string
  email: string
  currentStep: number
  expressStep: ExpressJointProgressStep
  completedSteps: number[]
  reachedSteps: number[]
  sections: ExpressJointSectionSaveItem[]
  status?: 'draft' | 'in_progress'
}): Promise<{ applicationId: string }> {
  const meta = {
    express_flow: EXPRESS_JOINT_NEW_FLOW,
    express_step: input.expressStep,
    completed_steps: input.completedSteps,
    reached_steps: input.reachedSteps,
    current_step: input.currentStep,
    last_saved_at: new Date().toISOString(),
  }

  let lastApplicationId = input.applicationId
  const response = await fetch('/api/applications/progress', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({
      email: input.email,
      applicationId: lastApplicationId,
      currentStep: input.currentStep,
      status: input.status ?? 'in_progress',
      applicationType: 'express',
      meta,
      sections: input.sections.map((sectionItem) => ({
        kind: sectionItem.kind,
        applicant: sectionItem.applicant,
        data: sectionItem.data,
        has_joint_applicant: sectionItem.has_joint_applicant,
      })),
    }),
  })
  const json = (await response.json().catch(() => null)) as {
    ok?: boolean
    error?: string
    data?: { applicationId?: string }
  } | null
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || 'Unable to save express application progress.')
  }
  if (typeof json.data?.applicationId === 'string') {
    lastApplicationId = json.data.applicationId
  }

  return { applicationId: lastApplicationId }
}

export type ExpressPlaidAccountDetails = {
  mask?: string
  routingLast4?: string
  accountLast4?: string
  name?: string
  institutionName?: string
  accountSubtype?: string
  routingNumber?: string
  accountNumber?: string
  identity?: unknown
}

export type ExpressSubmissionPayloadInput = {
  applicationId?: string | null
  email: string
  phone: string
  productId: string
  primaryExistingCustomer: boolean
  name: string
  dob: string
  ssn: string
  wantsJointApplicant: boolean
  jointExistingCustomer: boolean
  jointLookupToken?: string | null
  jointNeedsDetails: boolean
  jointAgreeToTerms: boolean
  jointBasicForSubmit: ExpressJointApplicantBasicInfo
  jointStreet: string
  jointApartment: string
  jointCity: string
  jointState: string
  jointZip: string
  jointDob: string
  jointGender: string
  jointSsn: string
  jointPersonaInquiryId: string | null
  jointPersonaVerified: boolean
  jointIdNumber: string
  jointIdState: string
  jointIdIssueDate: string
  jointIdExpirationDate: string
  jointIdAddressMatches: string
  jointEmployment: {
    employmentStatus: string
    employerName?: string
    jobTitle?: string
    annualIncome?: string
  }
  beneficiaries: BeneficiariesFormValues['beneficiaries']
  fundingSnapshot: ExpressFundingItem[]
  npsScore: number
  referralSource: string
  referralSpecify: string
  impactDonation?: ExpressJointProgressSnapshotInput['impactDonation']
  marketing: Record<string, unknown> | object
  expressCompletedSteps?: number[]
  expressReachedSteps?: number[]
  wizardSteps: ExpressJointProgressStep[]
}

/** Full express submit body — always sent to POST /api/applications/express. */
export function buildExpressSubmissionPayload(input: ExpressSubmissionPayloadInput): Record<string, unknown> {
  const nameParts = input.name.trim().split(/\s+/)
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''
  const email = input.email.trim().toLowerCase()
  const phoneDisplay = formatPhoneForBasicDisplay(input.phone)
  const totalFunding = input.fundingSnapshot.reduce(
    (sum, entry) => sum + parseFundingAmount(entry.amount),
    0,
  )
  const reviewStepNum = expressStepToNumber('review')
  const reachedSteps = input.jointNeedsDetails
    ? (input.expressReachedSteps?.length
      ? input.expressReachedSteps
      : input.wizardSteps.map((_, index) => index + 1))
    : [1, 2]
  const completedSteps = input.jointNeedsDetails
    ? (input.expressCompletedSteps?.length ? input.expressCompletedSteps : reachedSteps)
    : [1, 2]

  return {
    applicationId: input.applicationId || undefined,
    status: 'submitted',
    has_joint_applicant: input.wantsJointApplicant,
    submittedAt: new Date().toISOString(),
    jointLookupToken: input.wantsJointApplicant && input.jointExistingCustomer
      ? input.jointLookupToken ?? undefined
      : undefined,
    primary_applicant: input.primaryExistingCustomer
      ? {
          existingCustomer: true,
          basic_info: {
            agreeToTerms: true,
            email,
            phoneNumber: phoneDisplay,
          },
          personal_info: null,
          identity_info: null,
          employment_info: null,
        }
      : {
          existingCustomer: false,
          basic_info: {
            firstName,
            lastName,
            email,
            phoneNumber: phoneDisplay,
            agreeToTerms: true,
          },
          personal_info: {
            streetAddress: '',
            city: '',
            state: '',
            zipCode: '',
            dateOfBirth: input.dob,
            socialSecurityNumber: input.ssn,
          },
          identity_info: null,
          employment_info: null,
        },
    joint_applicant: input.wantsJointApplicant
      ? (input.jointExistingCustomer
        ? {
            existingCustomer: true,
            basic_info: {
              agreeToTerms: input.jointAgreeToTerms,
            },
            personal_info: null,
            identity_info: null,
            employment_info: null,
          }
        : {
            existingCustomer: false,
            basic_info: {
              firstName: input.jointBasicForSubmit.firstName,
              lastName: input.jointBasicForSubmit.lastName,
              email: input.jointBasicForSubmit.email,
              phoneNumber: input.jointBasicForSubmit.phoneNumber,
              agreeToTerms: input.jointBasicForSubmit.agreeToTerms,
            },
            personal_info: {
              streetAddress: input.jointStreet,
              apartmentUnit: input.jointApartment || undefined,
              city: input.jointCity,
              state: input.jointState,
              zipCode: input.jointZip,
              dateOfBirth: input.jointDob,
              gender: input.jointGender,
              socialSecurityNumber: input.jointSsn,
            },
            identity_info: {
              personaConsent: true,
              personaInquiryId: input.jointPersonaInquiryId || undefined,
              personaVerified: input.jointPersonaVerified,
              idNumber: input.jointIdNumber,
              idState: input.jointIdState,
              issueDate: input.jointIdIssueDate,
              expirationDate: input.jointIdExpirationDate,
              idAddressMatches: input.jointIdAddressMatches,
            },
            employment_info: input.jointEmployment.employmentStatus
              ? {
                  employmentStatus: input.jointEmployment.employmentStatus,
                  employerName: input.jointEmployment.employerName || undefined,
                  jobTitle: input.jointEmployment.jobTitle || undefined,
                  annualIncome: input.jointEmployment.annualIncome || undefined,
                }
              : null,
          })
      : null,
    beneficiaries: mapBeneficiariesForExpressPayload(input.beneficiaries),
    funding: {
      ...buildExpressFundingPayload(input.fundingSnapshot),
      total_initial_deposit: totalFunding.toFixed(2),
    },
    meta: input.jointNeedsDetails
      ? {
          express_flow: EXPRESS_JOINT_NEW_FLOW,
          express_step: 'review',
          completed_steps: completedSteps,
          reached_steps: reachedSteps,
          current_step: reviewStepNum,
          last_saved_at: new Date().toISOString(),
        }
      : {
          completed_steps: completedSteps,
          reached_steps: reachedSteps,
          current_step: reviewStepNum,
          last_saved_at: new Date().toISOString(),
        },
    marketing: input.marketing,
    nps_score: input.npsScore,
    referral_source: input.referralSource || null,
    referral_specify: input.referralSpecify.trim() || null,
    ...(input.impactDonation ? { impact_donation: input.impactDonation } : {}),
    email,
    product: input.productId,
  }
}

export async function loadExpressApplicationResumeSummary(applicationId: string): Promise<{
  applicationId: string
  summary: ExpressResumeSummary
  application?: Record<string, unknown> | null
  lastSavedAt?: string
}> {
  const response = await fetch(`/api/applications/progress?applicationId=${encodeURIComponent(applicationId)}`, {
    credentials: 'include',
    cache: 'no-store',
  })
  if (response.status === 401) {
    throw new Error('Please try again.')
  }
  if (response.status === 404) {
    throw new Error('Saved application could not be found.')
  }
  const json = (await response.json().catch(() => null)) as {
    ok?: boolean
    error?: string
    data?: {
      applicationId?: string
      summary?: ExpressResumeSummary
      application?: Record<string, unknown> | null
      lastSavedAt?: string
    }
  } | null
  if (!response.ok || !json?.ok || !json.data?.applicationId || !json.data.summary) {
    throw new Error(json?.error || 'Unable to load express application progress.')
  }
  if (json.data.summary.expressFlow !== EXPRESS_JOINT_NEW_FLOW) {
    throw new Error('This saved application is not an express joint flow.')
  }
  return {
    applicationId: json.data.applicationId,
    summary: json.data.summary,
    application: json.data.application ?? null,
    lastSavedAt: json.data.lastSavedAt,
  }
}

export {
  getExpressMissingSubmitMessage,
  getExpressMissingSubmitStep,
} from './express-missing-submit'

export type ExpressResumedSubmitResult =
  | { ok: true; applicationId: string }
  | { ok: false; error: string; missingParts?: string[] }

export async function submitExpressApplicationResumed(input: {
  applicationId: string
  email: string
  currentStep: number
  npsScore: number
  referralSource: string
  referralSpecify: string
}): Promise<ExpressResumedSubmitResult> {
  const response = await fetch('/api/applications/progress', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({
      email: input.email.trim().toLowerCase(),
      applicationId: input.applicationId,
      platform: 'consumer',
      currentStep: input.currentStep,
      status: 'submitted',
      applicationType: 'express',
      nps_score: input.npsScore,
      referral_source: input.referralSource || null,
      referral_specify: input.referralSpecify.trim() || null,
    }),
  })
  if (response.status === 401) {
    return { ok: false, error: 'Please try again.' }
  }
  const json = (await response.json().catch(() => null)) as {
    ok?: boolean
    error?: string
    data?: { applicationId?: string; status?: string; missingParts?: string[] }
  } | null
  if (!response.ok || !json?.ok) {
    return {
      ok: false,
      error: json?.error || 'Unable to submit application.',
      missingParts: json?.data?.missingParts,
    }
  }
  if (json.data?.status !== 'submitted') {
    return { ok: false, error: 'Submission not confirmed by server.' }
  }
  const applicationId = json.data?.applicationId || input.applicationId
  return { ok: true, applicationId }
}
